// Single-employee "add" modal (ADR-021 §3 admin-create). Mirrors ImportDialog's
// shell + mutation pattern. Consent is intentionally NOT collected here — an
// admin-created employee accepts PDPA at the mirror on first measurement (ADR-021
// §4), so we POST register without a consent object (the backend stores NULL).
//
// Every field is validated client-side against the SAME rules the backend
// enforces (employee_code regex, birth_year ≥12y, height/weight bounds, gender /
// bp_group enums) so the admin gets inline feedback before submit;
// the backend remains the source of truth (e.g. duplicate employee_code → 409).

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useI18n } from "@faceheart/i18n";
import type { EmployeeRegisterRequest, Site } from "@faceheart/api-types";
import { employeesApi } from "../../lib/api/endpoints";
import { ApiError } from "../../lib/api/client";
import { useAuth } from "../../lib/auth/AuthProvider";

type Gender = "male" | "female" | "other";
type BpGroup = "normal" | "hypertension";

const GENDER_VALUES: Gender[] = ["male", "female", "other"];
const BP_VALUES: BpGroup[] = ["normal", "hypertension"];

// Mirror the backend regexes (employees.go).
const CODE_RE = /^([0-9]{7}|[A-Za-z][0-9]{4})$/;

export function AddEmployeeDialog({
  sites,
  onClose,
}: {
  sites: Site[];
  onClose: () => void;
}) {
  const { t } = useI18n("admin");
  const qc = useQueryClient();

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState<Gender>("male");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [bpGroup, setBpGroup] = useState<BpGroup>("normal");
  const [siteId, setSiteId] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A site_admin can only create employees in their OWN site (ADR-027 v8): the
  // backend forces site_id to their scope, so we hide the site picker and show
  // the locked site (from /auth/me) instead. /v1/sites is 403 for a site_admin,
  // so we can't list sites for them anyway — user.site_name is the only source.
  const { user } = useAuth();
  const isSiteAdmin = user?.role === "site_admin";

  // Birth year must be 1900 .. currentYear-12 (ADR-031 / DB CHECK).
  const maxBirthYear = new Date().getFullYear() - 12;

  const mut = useMutation({
    mutationFn: (body: EmployeeRegisterRequest) => employeesApi.register(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success(t("employees.add.success"));
      onClose();
    },
    onError: (err) => {
      // 409 already_exists → a friendly "code taken" message; else generic.
      if (err instanceof ApiError && err.code === "already_exists") {
        setError(t("employees.add.duplicateCode"));
      } else if (err instanceof ApiError) {
        setError(t("employees.add.failed", { code: err.code }));
      } else {
        setError(t("common:errors.network"));
      }
    },
  });

  // Returns an inline error string, or null when the form is valid.
  function validate(): string | null {
    if (!code.trim()) return t("common:validation.employeeCodeEmpty");
    if (!CODE_RE.test(code.trim()))
      return t("common:validation.employeeCodeFormat");
    if (name.trim().length < 1 || name.trim().length > 50)
      return t("common:validation.nameLength");
    if (!birthYear) return t("common:validation.birthYear");
    {
      const y = Number(birthYear);
      if (!Number.isInteger(y) || y < 1900)
        return t("common:validation.birthYearBefore1900");
      if (y > maxBirthYear) return t("common:validation.minAge12");
    }
    const h = Number(height);
    if (!Number.isFinite(h) || h < 50 || h > 250)
      return t("common:validation.heightRange");
    const w = Number(weight);
    if (!Number.isFinite(w) || w < 20 || w > 250)
      return t("common:validation.weightRange");
    return null;
  }

  function submit() {
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const body: EmployeeRegisterRequest = {
      employee_code: code.trim(),
      name: name.trim(),
      birth_year: Number(birthYear),
      gender,
      height_cm: Number(height),
      weight_kg: Number(weight),
      bp_group: bpGroup,
      // site_admin: omit site_id — the backend forces it to their own site.
      // global admin: send the chosen site (if any).
      ...(!isSiteAdmin && siteId ? { site_id: siteId } : {}),
    };
    mut.mutate(body);
  }

  return (
    <Backdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("employees.add.title")}
        className="w-140 max-w-[92vw] max-h-[88vh] overflow-y-auto rounded-lg border border-admin-border bg-admin-bg2 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-medium">{t("employees.add.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-admin-text3 hover:text-admin-text1"
            aria-label={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("common:labels.employeeCode")} required>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="A1234"
              className={inputCls}
            />
          </Field>
          <Field label={t("common:labels.name")} required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              className={inputCls}
            />
          </Field>
          <Field label={t("common:labels.birthYear")} required>
            <input
              type="number"
              inputMode="numeric"
              value={birthYear}
              max={maxBirthYear}
              min={1900}
              placeholder="1990"
              onChange={(e) => setBirthYear(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label={t("common:labels.gender")} required>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender)}
              className={inputCls}
            >
              {GENDER_VALUES.map((v) => (
                <option key={v} value={v}>
                  {t(`common:gender.${v}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("common:labels.height")} required>
            <input
              type="number"
              inputMode="numeric"
              min={50}
              max={250}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label={t("common:labels.weight")} required>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min={20}
              max={250}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label={t("common:labels.bpGroup")} required>
            <select
              value={bpGroup}
              onChange={(e) => setBpGroup(e.target.value as BpGroup)}
              className={inputCls}
            >
              {BP_VALUES.map((v) => (
                <option key={v} value={v}>
                  {t(`common:bpGroup.${v}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("employees.tableHeaders.site")}>
            {isSiteAdmin ? (
              // Locked to the site_admin's own site (backend enforces this).
              <div
                className={`${inputCls} flex items-center text-admin-text2`}
                aria-label={t("employees.tableHeaders.site")}
              >
                {user?.site_name ?? "—"}
              </div>
            ) : (
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                className={inputCls}
              >
                <option value="">{t("employeeDetail.siteUnassigned")}</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        {error && <p className="mt-3 text-sm text-admin-red">{error}</p>}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-admin-border px-4 py-2 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {t("common:actions.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={mut.isPending}
            className="rounded bg-admin-cyan/90 px-4 py-2 text-sm font-medium text-admin-bg hover:bg-admin-cyan disabled:opacity-50"
          >
            {mut.isPending
              ? t("employees.add.submitting")
              : t("employees.add.submit")}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

const inputCls =
  "w-full rounded border border-admin-border bg-admin-bg3 px-3 py-2 text-sm text-admin-text1 placeholder:text-admin-text3 focus:outline-none focus:border-admin-cyan";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-admin-text3">
        {label}
        {required && <span className="text-admin-red"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Backdrop({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}
